/**
 * Presentation seam for `contacts_interactions.activity_type`.
 *
 * The value set, each value's glyph name, its Tailwind colour pair and whether a
 * user may log it by hand all come from
 * `shared/config/interaction_types.config.json` via the generated `config-enums`
 * bindings — never re-typed here. Before that config existed the icon/colour map
 * was a literal in `ActivityLog.tsx` covering 21 of the 32 values, so the other
 * 11 rendered a generic sticky note with nothing anywhere saying they had been
 * missed.
 *
 * What this file DOES own is the one thing a JSON config cannot carry: the
 * icon-NAME → component table. It is deliberately keyed on the glyph name and
 * not on the interaction type, which collapses the hand-maintained surface from
 * 32 rows to 12 and makes a new interaction type reusing an existing glyph free.
 * The shared `iconMap` cannot serve it — that map is typed `LucideIcon` and
 * lucide dropped brand marks, so `WhatsAppIcon` has no place in it.
 */
import React from 'react';
import {
  Calendar,
  CheckSquare,
  Edit,
  Inbox,
  Mail,
  Package,
  Phone,
  Plus,
  Send,
  StickyNote,
  Tag,
  User,
  Handshake,
} from 'lucide-react';

import {
  INTERACTION_TYPE_VALUES,
  interactionTypeColor,
  interactionTypeIcon,
  interactionTypeManualLoggable,
  type InteractionType,
} from '../../../generated/config-enums';
import { WhatsAppIcon } from '../icons/WhatsAppIcon';

/** Renders at the timeline's inline glyph size. */
const ICON_CLASS = 'h-4 w-4';

type IconComponent = React.ComponentType<{ className?: string }>;

/**
 * Every glyph name the config may carry. Exported so its own coverage test can
 * assert the config names nothing this table lacks — a miss would otherwise fall
 * through to the sticky-note default and look exactly like a deliberate choice.
 */
export const INTERACTION_TYPE_ICON_COMPONENTS: Record<string, IconComponent> = {
  StickyNote,
  Mail,
  Phone,
  Calendar,
  CheckSquare,
  Send,
  Inbox,
  Handshake,
  User,
  Edit,
  Plus,
  WhatsApp: WhatsAppIcon,
  // Glyphs a TENANT type may choose. `Tag` is also the `custom` discriminator's own
  // config icon — the treatment a tenant-typed row falls back to when its type
  // cannot be resolved (an older tab, a type the caller may not read).
  Tag,
  Package,
};

/**
 * The interaction types `InteractionFormDialog` offers and `InteractionLog` lets
 * a user edit — derived from the config's `manual_loggable` flag, which is the
 * same flag the generator uses to write `interaction_update.schema.json`'s enum.
 * The form's picker and the backend's accepted set therefore cannot drift apart.
 */
export const MANUAL_INTERACTION_TYPES: readonly InteractionType[] =
  INTERACTION_TYPE_VALUES.filter((type) => interactionTypeManualLoggable[type]);

const MANUAL_INTERACTION_TYPE_SET: ReadonlySet<string> = new Set(MANUAL_INTERACTION_TYPES);

/**
 * The `activity_type` value that means "the kind of this row is in
 * `interaction_type_id`" — the tenant's own vocabulary, which is table-backed and
 * therefore cannot be a member of the generated union above.
 */
export const CUSTOM_INTERACTION_TYPE = 'custom';

/**
 * What the log dialog actually OFFERS: the manual built-ins, minus the discriminator.
 *
 * `custom` has to be `manual_loggable` twice over — it is the value the dialog POSTs
 * for a tenant-typed interaction, and it is what `isManualInteractionType` reads to
 * decide whether a logged row may be edited or deleted by its author. Neither works
 * if the config flag is false. But it must never render as a bare "Custom" row beside
 * Call and Meeting: it names no activity, and posting it alone is a row the database's
 * pairing constraint rejects. The tenant's types are offered as their own group
 * instead. Pinned from the backend side in
 * `modules/master_data/contacts/__tests__/test_interaction_type_single_source.py`.
 */
export const OFFERED_BUILTIN_INTERACTION_TYPES: readonly InteractionType[] =
  MANUAL_INTERACTION_TYPES.filter((type) => type !== CUSTOM_INTERACTION_TYPE);

/** Whether this interaction was logged by hand, and so may be edited or deleted. */
export function isManualInteractionType(type: string): boolean {
  return MANUAL_INTERACTION_TYPE_SET.has(type);
}

/** The subset of a tenant interaction type this seam needs to render one. */
export interface TenantInteractionTypeDisplay {
  icon?: string | null;
  color?: string | null;
}

/**
 * The glyph and colour for one interaction type.
 *
 * `type` is a plain `string` because it arrives off the API, where the column is
 * a `VARCHAR` — a value this build does not know about (an older tab against a
 * newer backend) falls back to the neutral note treatment rather than rendering
 * nothing.
 *
 * `tenantType` is the resolved row when `type` is the `custom` DISCRIMINATOR. Its
 * colour is a tenant-chosen `#RRGGBB` rather than a Tailwind token pair, so it cannot
 * ride in `color` — it comes back as `style` for the caller to spread, and the token
 * pair it replaces is the neutral one, which reads in both light and dark mode. An
 * UNRESOLVED tenant type (an older tab, a retired type the caller did not fetch) falls
 * back to the discriminator's own config treatment rather than rendering blank.
 */
export function getInteractionTypeDisplay(
  type: string,
  tenantType?: TenantInteractionTypeDisplay | null
): { icon: React.ReactNode; color: string; style?: React.CSSProperties } {
  const iconName = tenantType?.icon || interactionTypeIcon[type as InteractionType];
  const color = interactionTypeColor[type as InteractionType];
  const IconComponent = INTERACTION_TYPE_ICON_COMPONENTS[iconName] ?? StickyNote;

  return {
    icon: <IconComponent className={ICON_CLASS} />,
    color: tenantType?.color ? 'text-foreground' : (color ?? 'bg-muted text-muted-foreground'),
    // A tenant colour is a stored hex the admin picked, so it is DATA, not a theme
    // token — the one legitimate inline style here. Kept translucent so a
    // low-contrast pick still reads against both themes.
    style: tenantType?.color ? { backgroundColor: `${tenantType.color}26` } : undefined,
  };
}
